import { atom } from 'jotai'
import type { Atom } from 'jotai'
import type { MediumArea } from '~/features/facets/types'
import { isPC$ } from '~/lib/ua'
import type { GenreMap, Spot } from '../../../facets'
import { tagsMap } from '../../sd/preferences/tags'
import { specialFeature$ } from '../specialFeature'
import { timesale$ } from '../timesale'
import type { SearchCriteria } from '../validate'
import { areaCodesToFacets } from './area'
import { genreCodeToFacet } from './genre'
import { keywordToFacet } from './keyword'
import { pointRewardToFacet } from './pointReward'
import { addPricePreferences, preferences, privateRoom } from './preferences'
import { specialFeatureToFacet } from './specialFeature'
import { spotIdToFacet } from './spot'
import { tagMap$, tagToFacet } from './tags'
import { createTimesaleSelected } from './timesale'
import type { Facet } from './type'

// mediumAreaMap$ と genreMap$ は直接 import すると Vite が module resolution で失敗する...
export function createSelected$(
  criteria$: Atom<SearchCriteria | undefined>,
  mediumArea$: Atom<MediumArea | undefined>,
  genreMap$: GenreMap,
  spot$: Atom<Spot | undefined>,
  inSpotOnly$: Atom<boolean>,
  prefix: string
): Atom<readonly Facet[]> {
  const selectedAreas$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$)
    const { largeAreaCode, mediumAreaCode, smallAreaCodes, minimumAreaCodes } = criteria ?? {}
    if (largeAreaCode === undefined || mediumAreaCode === undefined) {
      return []
    }
    const mediumArea = get(mediumArea$)
    return areaCodesToFacets(mediumArea, criteria)
  })
  selectedAreas$.debugLabel = `selected-${prefix}-selectedAreas$`

  const selectedSpot$ = atom<Facet | undefined>((get) => {
    const criteria = get(criteria$)
    const { spotId } = criteria ?? {}
    if (spotId === undefined) {
      return undefined
    }
    const spot = get(spot$)
    if (spot === undefined) {
      return undefined
    }
    const inSpotOnly = get(inSpotOnly$)
    return spotIdToFacet(spot, inSpotOnly)
  })
  selectedSpot$.debugLabel = `selected-${prefix}-selectedSpot$`

  const selectedGenres$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$)
    const { genreCodes } = criteria ?? {}
    if (genreCodes === undefined || genreCodes.length === 0) {
      return []
    }
    const map = get(genreMap$)
    const genres = genreCodes
      .map((code) => genreCodeToFacet(code, map))
      .filter((g) => g !== undefined)
      .filter((g) => g.showInList(criteria))
    // alphabet の範囲だから locale 指定なしの localCompare で大丈夫
    return genres.toSorted((a, b) => a.facetKey.localeCompare(b.facetKey))
  })
  selectedGenres$.debugLabel = `selected-${prefix}-selectedGenres$`

  const selectedOthers$ = createSelectedOthers(criteria$, prefix)
  selectedOthers$.debugLabel = `selected-${prefix}-selectedOthers$`

  const selected$ = atom((get) => {
    const criteria = get(criteria$)
    const selectedSpot = get(selectedSpot$)
    const preferences = [
      ...get(selectedAreas$),
      ...(selectedSpot !== undefined ? [selectedSpot] : []),
      ...get(selectedGenres$),
      ...get(selectedOthers$),
    ]
    return addPricePreferences(preferences, criteria)
  })
  selected$.debugLabel = `selected-${prefix}-selected$`

  return selected$
}

export function createSelectedOthers(criteria$: Atom<SearchCriteria | undefined>, prefix: string) {
  const tagKeys = ['sceneTagIds', 'foodTagIds', 'genreTagIds', 'drinkTagIds'] as const
  const selectedTags$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$) ?? {}
    const result: Facet[] = []
    for (const key of tagKeys) {
      for (const tagId of criteria[key] ?? []) {
        const found = tagsMap.get(tagId)
        if (found !== undefined) {
          result.push(tagToFacet(found))
        } else {
          // 検索の UI 上に表示されないタグが検索条件に含まれている場合
          // loader で取得したタグ情報を使用して Facet を作成する
          const tag = get(tagMap$)[String(tagId)]
          if (tag !== undefined) {
            result.push(tagToFacet(tag))
          }
        }
      }
    }
    return result
  })
  selectedTags$.debugLabel = `selected-${prefix}-selectedTags$`

  const selectedPreferences$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$) ?? {}
    return [...Object.values(preferences), privateRoom].filter((p) => p.showInList(criteria))
  })
  selectedPreferences$.debugLabel = `selected-${prefix}-selectedPreferences$`

  const selectedKeywords$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$) ?? {}
    const keywords = criteria.keywords ?? []
    return keywords.map((keyword) => keywordToFacet(keyword))
  })
  selectedKeywords$.debugLabel = `selected-${prefix}-selectedKeywords$`

  const selectedTimesales$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$) ?? {}
    const { isTimesale, timesaleId } = criteria
    const timesale = get(timesale$)
    return createTimesaleSelected(isTimesale ?? false, timesaleId, timesale)
  })

  const selectedPointRewards$ = atom<Facet[]>((get) => {
    const criteria = get(criteria$) ?? {}
    const { isIkyuDays } = criteria
    return pointRewardToFacet(isIkyuDays)
  })

  const selectedSpecial$ = atom<Facet[]>((get) => {
    const special = get(specialFeature$)
    return specialFeatureToFacet(special)
  })

  const selectedOthers$ = atom((get) => {
    const pc = get(isPC$)
    return [
      ...get(selectedTags$),
      ...get(selectedPreferences$),
      // リストの絞り込み条件つぶにはフリーワードを表示しない
      // @see: https://ikyu.atlassian.net/wiki/spaces/RESUIUX/pages/6898221072/QA#%E5%87%BA%E3%81%9F%E6%84%8F%E8%A6%8B
      ...(pc || prefix !== 'applied' ? get(selectedKeywords$) : []),
      ...get(selectedTimesales$),
      ...get(selectedPointRewards$),
      ...get(selectedSpecial$),
    ]
  })
  selectedOthers$.debugLabel = `selected-${prefix}-selectedOthers$`

  return selectedOthers$
}
